import React from 'react';

import CleanBanner from '@/components/corporate/CleanBanner';
import Seo from '@/components/seo/Seo';
import HowItWorksDiagram from '@/views/product/datamap/HowItWorksDiagram';
import { ROUTE_SEO, buildProductLd } from '@/config/seo';

// ── 방사형(레벨형) 연관관계·유사도 네트워크 그래프 ─────────────────────────
// 중심 → L1 → L2 → L3 동심 배치. 인접 레벨끼리만 연결(레벨 건너뛰는 선 없음).
// 노드는 멀티컬러 + 1.5배 반경 faint halo. 유사도 엣지는 점선 시안.
const DC = {
  blue: '#2B8CE6',
  blue2: '#1F6FD6',
  lblue: '#4FB0F0',
  orange: '#F2A33C',
  green: '#5FAE3D',
  navy: '#1F5A78',
  purple: '#4A43C7',
};

interface GNode {
  x: number;
  y: number;
  r: number;
  c: string;
  l?: string;
  center?: boolean;
}
type GEdge = [number, number, boolean]; // [from, to, isSimilarity]

function buildGraph() {
  const cx = 320;
  const cy = 295;
  const R1 = 118;
  const R2 = 210;
  const R3 = 285;
  const pol = (a: number, r: number) => ({
    x: cx + r * Math.cos((a * Math.PI) / 180),
    y: cy + r * Math.sin((a * Math.PI) / 180),
  });

  const nodes: GNode[] = [{ x: cx, y: cy, r: 34, c: DC.blue, center: true }];
  const edges: GEdge[] = [];
  const addN = (x: number, y: number, r: number, c: string, l?: string) => {
    nodes.push({ x, y, r, c, l });
    return nodes.length - 1;
  };

  const L1: Array<{ a: number; c: string; r: number; l: string; d?: boolean }> = [
    { a: -92, c: DC.orange, r: 26, l: '관광 인구', d: true },
    { a: -32, c: DC.navy, r: 24, l: '국민 건강 통계' },
    { a: 26, c: DC.green, r: 20, l: '폐암 사망률' },
    { a: 92, c: DC.blue2, r: 24, l: '금정 위치정보', d: true },
    { a: 150, c: DC.green, r: 26, l: '도시율 조정 현황' },
    { a: 214, c: DC.lblue, r: 25, l: '대기오염정보 조정', d: true },
  ];
  const i1 = L1.map((n) => {
    const p = pol(n.a, R1);
    const i = addN(p.x, p.y, n.r, n.c, n.l);
    edges.push([0, i, !!n.d]);
    return i;
  });

  // [parentIdxInL1, angle, color, r, label, isSimilarity?]
  const L2: Array<[number, number, string, number, string, boolean?]> = [
    [0, -114, DC.green, 14, '암 발병률'],
    [0, -72, DC.orange, 16, '외국인 통계'],
    [1, -46, DC.navy, 12, '어린이 통계'],
    [1, -16, DC.green, 11, '건강검진기관'],
    [2, 24, DC.green, 13, '폐렴 발병률'],
    [3, 80, DC.blue, 14, '경정 위치정보', true],
    [3, 108, DC.navy, 11, '서울 산림'],
    [4, 138, DC.orange, 15, '발전소 대기'],
    [4, 166, DC.green, 12, '서울 도시율'],
    [5, 196, DC.lblue, 13, '도시 현황'],
    [5, 230, DC.navy, 11, '진로 산림'],
  ];
  const i2 = L2.map((s) => {
    const p = pol(s[1], R2);
    const i = addN(p.x, p.y, s[3], s[2], s[4]);
    edges.push([i1[s[0]], i, !!s[5]]);
    return i;
  });

  // [parentIdxInL2, angle, color, label]
  // 최외곽 링이 듬성해 보이지 않도록 일부 부모에 2번째 자식을 달고,
  // 자식이 없던 부모(2·8)에도 노드를 추가해 각도 간격(≥20°)을 고르게 채운다.
  const L3: Array<[number, number, string, string]> = [
    [0, -122, DC.green, '등산로 정보'],
    [0, -100, DC.lblue, ''],
    [1, -62, DC.navy, '국민 통계'],
    [2, -42, DC.orange, ''],
    [3, -10, DC.green, ''],
    [4, 28, DC.green, ''],
    [4, 50, DC.blue, ''],
    [5, 72, DC.blue2, ''],
    [5, 92, DC.green, ''],
    [6, 112, DC.orange, ''],
    [7, 132, DC.green, ''],
    [8, 162, DC.navy, ''],
    [9, 192, DC.lblue, ''],
    [9, 212, DC.lblue, ''],
    [10, 234, DC.navy, ''],
  ];
  L3.forEach((s) => {
    const p = pol(s[1], R3);
    const i = addN(p.x, p.y, 7, s[2], s[3]);
    edges.push([i2[s[0]], i, false]);
  });

  return { nodes, edges, cx, cy, rings: [R1, R2, R3] };
}

const MapGraph: React.FC = () => {
  const { nodes, edges, cx, cy, rings } = buildGraph();
  return (
    <svg
      viewBox="0 0 640 600"
      style={{ width: '100%', maxWidth: 480, height: 'auto', overflow: 'visible' }}
      aria-hidden
    >
      {/* 레벨 위치를 나타내는 점선 링 */}
      {rings.map((r, i) => (
        <circle
          key={`ring-${i}`}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="rgba(120,135,155,.32)"
          strokeWidth={1}
          strokeDasharray="2 6"
        />
      ))}
      {/* faint halo */}
      {nodes.map((n, i) => (
        <circle key={`halo-${i}`} cx={n.x} cy={n.y} r={n.r * 1.5} fill={n.c} opacity={0.16} />
      ))}
      {/* 엣지 — 연관(실선 그레이) / 유사도(점선 시안) */}
      {edges.map((e, i) => {
        const a = nodes[e[0]];
        const b = nodes[e[1]];
        return (
          <line
            key={`edge-${i}`}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={e[2] ? 'rgba(79,176,240,.9)' : 'rgba(120,135,155,.45)'}
            strokeWidth={e[2] ? 1.8 : 1.2}
            strokeDasharray={e[2] ? '5 6' : undefined}
          />
        );
      })}
      {/* 노드 */}
      {nodes.map((n, i) => (
        <circle key={`node-${i}`} cx={n.x} cy={n.y} r={n.r} fill={n.c} />
      ))}
      {/* 라벨 */}
      {nodes.map((n, i) => {
        if (n.center) {
          return (
            <React.Fragment key={`label-${i}`}>
              <text x={n.x} y={n.y - 2} fill="#fff" fontSize={12} fontWeight={800} textAnchor="middle">
                미세먼지
              </text>
              <text x={n.x} y={n.y + 14} fill="#fff" fontSize={12} fontWeight={800} textAnchor="middle">
                정보
              </text>
            </React.Fragment>
          );
        }
        if (n.l) {
          return (
            <text
              key={`label-${i}`}
              x={n.x}
              y={n.y - n.r - 6}
              fill="#48586a"
              fontSize={10.5}
              fontWeight={600}
              textAnchor="middle"
            >
              {n.l}
            </text>
          );
        }
        return null;
      })}
    </svg>
  );
};

const DataMapMain: React.FC = () => {
  return (
    <div className="cp">
      <Seo
        title={ROUTE_SEO.datamap.title}
        description={ROUTE_SEO.datamap.description}
        path={ROUTE_SEO.datamap.path}
        jsonLd={buildProductLd('datamap', 'DataMap')}
      />
      <CleanBanner eyebrow="PRODUCT" title="Data Map" desc="데이터맵 · 메타데이터 연계 시각화" />

      {/* 소개 — WHAT IS IT (연관관계·유사도 맵을 우측 배경으로 연하게) */}
      <section className="cp-section cp-intro-hero">
        <div className="cp-intro-hero__bg" aria-hidden>
          <MapGraph />
        </div>
        <div className="cp__container">
          <div className="cp-intro-hero__copy">
            <div className="cp-eyebrow">WHAT IS IT</div>
            <h2>데이터맵이란?</h2>
            <p>
              서로 다른 데이터 간의 <b className="cp-emph">연관관계와 유사도</b>를 분석해 한눈에 보여주는{' '}
              <b className="cp-emph">데이터 시각화 도구</b>입니다. 연계된 모든 메타데이터의 관계망을 직관적으로 조망할
              수 있습니다.
            </p>
          </div>
        </div>
      </section>

      {/* 구성 단계 — HOW IT WORKS 3계층 다이어그램 */}
      <section className="cp-section cp-section--soft cp-section--tight cp-section--clip">
        <div className="cp__container">
          <HowItWorksDiagram />
        </div>
      </section>
    </div>
  );
};

export default DataMapMain;
